import { useState, useEffect, useCallback } from 'react';
import { eventApi } from '../services/api';

/**
 * Hook to fetch events for a specific date
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
 * @param {boolean} enabled - When false, skip fetching (e.g. before authentication)
 */
export const useEventData = (date = null, enabled = true) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      console.log(`Fetching events for date: ${targetDate}`);

      // Use date-specific endpoint if date is provided, otherwise use today's endpoint
      const response = date
        ? await eventApi.getEventsByDate(date)
        : await eventApi.getTodayEvents();

      // Axios returns { data: ... }
      const result = response.data;

      // Extract events array from response
      const eventsData = result.data || result.events || result || [];

      // Transform API data to match EventMarker expected format
      const transformedEvents = eventsData.map(event => ({
        id: event.id,
        ronda_id: event.ronda_id,
        location: {
          latitude: event.latitude,
          longitude: event.longitude
        },
        entity: {
          name: event.entity_name
        },
        tipo: event.tipo,
        local: event.local,
        pessoas_assistidas: event.pessoas_assistidas,
        numero_refeicoes: event.numero_refeicoes || 0,
        ronda_name: event.ronda_name,
        created_at: event.created_at
      }));

      console.log(`Received ${transformedEvents.length} events for ${targetDate}`);
      setEvents(transformedEvents);
      setLastUpdate(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  const addEvent = useCallback((newEvent) => {
    setEvents(currentEvents => [newEvent, ...currentEvents]);
    setLastUpdate(new Date().toISOString());
  }, []);

  // Fetch events when component mounts or date changes
  useEffect(() => {
    if (!enabled) return;
    fetchEvents();
  }, [fetchEvents, enabled]);

  return {
    events,
    loading,
    error,
    lastUpdate,
    refreshEvents: fetchEvents,
    addEvent
  };
};